import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Card, CardContent, CardHeader, Switch } from '@roger/ui-components';
import { FileInput } from 'lucide-react';

import { setPatientImportBadgePatientsPatientIdImportBadgePatch as setPatientImportBadge } from '../../services/api/sdk.gen';
import type { PatientResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateInAgencyZone } from '../../utils/dateUtils';

interface PatientImportBadgeToggleProps {
  patientId: string;
  /** ISO timestamp the chart was imported (drives the copy's date). */
  importedAt?: string | null;
  /** True while the badge is demoted/hidden. */
  badgeHidden: boolean;
  /** Called with the server's updated patient after a confirmed change. */
  onUpdated: (patient: PatientResponse) => void;
}

/**
 * The "Imported Chart" section of the Patient Info tab (EMR-1177).
 *
 * Rendered only for imported charts, directly above the test-patient toggle
 * and mirroring its shape: copy above the switch, available to every
 * clinician, pessimistic (the switch reflects server state only). Demotion
 * hides the badge everywhere; the provenance stays on the chart, and this
 * card remaining visible is itself the residual indicator that the chart
 * came from an external system.
 */
export function PatientImportBadgeToggle({
  patientId,
  importedAt,
  badgeHidden,
  onUpdated,
}: PatientImportBadgeToggleProps) {
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [saving, setSaving] = useState(false);

  const applyChange = async (showBadge: boolean) => {
    setSaving(true);
    try {
      const { data } = await setPatientImportBadge({
        path: { patient_id: patientId },
        body: { hidden: !showBadge },
        throwOnError: true,
      });
      // Pessimistic: reflect server truth only after a confirmed save.
      if (data) onUpdated(data);
    } catch (err) {
      console.error('Failed to update the Imported badge', err);
      showError(
        getBusinessErrorMessage(err) ?? 'Failed to update the Imported badge'
      );
    } finally {
      setSaving(false);
    }
  };

  // imported_at is a UTC timestamp; the calendar-date helper would keep the
  // UTC day and date an evening import a day late. Same fix as the badge.
  const importedOn = importedAt
    ? ` on ${formatDateInAgencyZone(importedAt)}`
    : '';

  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className='flex items-center gap-2'>
          <FileInput className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Imported Chart</h3>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          This chart was imported from External EMR{importedOn}. The badge marks
          it until records are verified. Hide the badge once care is established
          in Roger. The chart is always tied to its import record on file.
        </p>
        <div className='flex items-center gap-3'>
          <Switch
            id='patient-import-badge-toggle'
            checked={!badgeHidden}
            disabled={saving}
            onCheckedChange={checked => void applyChange(checked)}
            aria-label='Show the Imported badge'
          />
          <label
            htmlFor='patient-import-badge-toggle'
            className='cursor-pointer text-sm'
          >
            Show the Imported badge
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
